import { useState } from "react";
import { MapPin, Phone, Mail, Clock, ArrowUpRight, MessageCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useContent } from "../contexts/ContentContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const { getPage } = useContent();
  const c = getPage("contact", {});
  const brand = getPage("branding", {});

  const offices = c.offices || [];
  const whatsappNum = brand.whatsapp_number || "911244567890";
  const whatsappMsg = encodeURIComponent(brand.whatsapp_message || "Hi, I'd like to schedule a consultation.");

  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/leads`, { ...form, source: "contact-page" });
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch { setError("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">{c.hero_label || "Get in Touch"}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight max-w-3xl">{c.hero_headline || "Let's start a conversation."}</h1>
          <p className="text-white/60 font-sans text-base md:text-lg mt-6 max-w-2xl leading-relaxed">{c.hero_subtext || ""}</p>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            <div className="lg:col-span-3">
              <h2 className="font-serif text-3xl text-navy-900 mb-2">{c.form_headline || "Send us a message"}</h2>
              <p className="text-gray-500 font-sans text-sm mb-8">{c.form_subtext || "Fill out the form below and our team will get back to you within 24 hours."}</p>
              {submitted ? (
                <div data-testid="contact-success-message" className="bg-gray-50 border border-gray-200 p-12 text-center">
                  <div className="w-12 h-12 bg-gold-500 rounded-full flex items-center justify-center mx-auto mb-4"><ArrowUpRight className="w-6 h-6 text-navy-900" /></div>
                  <h3 className="font-serif text-2xl text-navy-900 mb-2">Thank you for reaching out.</h3>
                  <p className="text-gray-500 font-sans text-sm">A member of our team will be in touch shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div><Label htmlFor="name" className="text-gray-700 font-sans text-sm font-medium mb-2 block">Full Name *</Label><Input id="name" data-testid="contact-name-input" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border-gray-200 rounded-sm h-12 font-sans" placeholder="Your name" /></div>
                    <div><Label htmlFor="email" className="text-gray-700 font-sans text-sm font-medium mb-2 block">Email *</Label><Input id="email" data-testid="contact-email-input" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="border-gray-200 rounded-sm h-12 font-sans" placeholder="your@email.com" /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div><Label htmlFor="phone" className="text-gray-700 font-sans text-sm font-medium mb-2 block">Phone</Label><Input id="phone" data-testid="contact-phone-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="border-gray-200 rounded-sm h-12 font-sans" placeholder="+91 XXXXX XXXXX" /></div>
                    <div><Label htmlFor="company" className="text-gray-700 font-sans text-sm font-medium mb-2 block">Company</Label><Input id="company" data-testid="contact-company-input" value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="border-gray-200 rounded-sm h-12 font-sans" placeholder="Your company" /></div>
                  </div>
                  <div><Label htmlFor="message" className="text-gray-700 font-sans text-sm font-medium mb-2 block">Message *</Label><Textarea id="message" data-testid="contact-message-input" required value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="border-gray-200 rounded-sm font-sans min-h-[120px]" placeholder="Tell us about your requirements..." /></div>
                  {error && <p data-testid="contact-error-message" className="text-red-500 font-sans text-sm">{error}</p>}
                  <button type="submit" data-testid="contact-submit-button" disabled={submitting} className="inline-flex items-center gap-2 bg-navy-900 text-white px-8 py-4 rounded-sm font-sans text-sm font-medium hover:bg-navy-800 transition-colors duration-300 disabled:opacity-50">{submitting ? "Sending..." : "Send Message"}{!submitting && <ArrowUpRight className="w-4 h-4" />}</button>
                </form>
              )}
            </div>
            <div className="lg:col-span-2">
              <div className="space-y-8">
                {offices.map(o => (
                  <div key={o.city} className="border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-gold-500" strokeWidth={1.5} />
                      <h3 className="font-serif text-lg text-navy-900">{o.city}</h3>
                      <span className="text-xs font-sans text-gold-500 bg-gold-500/10 px-2 py-0.5">{o.tag}</span>
                    </div>
                    <p className="text-gray-500 font-sans text-sm mb-3">{o.address}</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-gray-500 font-sans text-sm"><Phone className="w-3.5 h-3.5 text-gold-500" strokeWidth={1.5} /> {o.phone}</div>
                      <div className="flex items-center gap-2 text-gray-500 font-sans text-sm"><Mail className="w-3.5 h-3.5 text-gold-500" strokeWidth={1.5} /> {o.email}</div>
                      <div className="flex items-center gap-2 text-gray-500 font-sans text-sm"><Clock className="w-3.5 h-3.5 text-gold-500" strokeWidth={1.5} /> {o.hours}</div>
                    </div>
                  </div>
                ))}
                <a href={`https://wa.me/${whatsappNum}?text=${whatsappMsg}`} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-link" className="flex items-center gap-3 bg-green-600 text-white p-4 rounded-sm hover:bg-green-700 transition-colors">
                  <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
                  <div><p className="font-sans text-sm font-medium">Chat on WhatsApp</p><p className="text-white/70 text-xs font-sans">Instant response during business hours</p></div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
